import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type HTMLAttributes,
} from "react";
import {
  AssistantRuntimeProvider,
  SimpleImageAttachmentAdapter,
  useAui,
  useLocalRuntime,
  useRemoteThreadListRuntime,
  useRuntimeAdapters,
  type ChatModelAdapter,
  type ThreadMessage,
} from "@assistant-ui/react";
import { createLocalStorageAdapter } from "@assistant-ui/core/react";
import { MessagesSquare, PanelLeft, Settings2, Wrench, X } from "lucide-react";
import { Thread } from "@/components/assistant-ui/elements/thread.aui";
import { ThreadList } from "@/components/assistant-ui/elements/thread-list.aui";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { AgentConfig, Lead, LeadChange } from "../leads/types";
import {
  accountStorageScope,
  getChatStorage,
  withMessageDeletion,
} from "./chatStorage";
import { ReceiptProvider, restoreContacts } from "./LeadReceipt";
import { loadReceipts } from "./receiptStore";
import { executeAgentResult, runRemoteAgent, type AgentMode } from "./runAgent";

function firstText(messages: readonly ThreadMessage[]): string {
  for (const message of messages) {
    if (message.role !== "user") continue;
    const text = message.content.find((part) => part.type === "text");
    if (text?.type === "text" && text.text.trim())
      return text.text.trim().replace(/\s+/g, " ").slice(0, 28);
    if (message.content.some((part) => part.type === "image"))
      return "图片盘口整理";
  }
  return "新会话";
}

function AgentWelcome() {
  return null;
}

const threadComponents = { Welcome: AgentWelcome };

export function AgentWorkspace({
  leads,
  owner,
  apply,
  onClose,
  dragHandleProps,
}: {
  leads: Lead[];
  owner: string;
  apply: (changes: LeadChange[]) => Promise<void>;
  onClose: () => void;
  dragHandleProps?: HTMLAttributes<HTMLElement>;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [mode, setMode] = useState<AgentMode>(() =>
    localStorage.getItem("huopan-agent-mode") === "chat" ? "chat" : "work",
  );
  const [config, setConfig] = useState<AgentConfig>(() => ({
    baseUrl: "/agent-api/v1",
    apiKey: "",
    model: localStorage.getItem("huopan-agent-model") || "deepseek-v4.1-flash",
  }));

  useEffect(() => {
    localStorage.removeItem("huopan-agent-key");
    sessionStorage.removeItem("huopan-agent-key");
    localStorage.removeItem("huopan-agent-base");
    void loadReceipts(owner);
  }, [owner]);

  useEffect(() => {
    localStorage.setItem("huopan-agent-mode", mode);
  }, [mode]);

  const saveConfig = (next: AgentConfig) => {
    setConfig(next);
    localStorage.setItem("huopan-agent-model", next.model);
  };

  const storagePrefix = `${accountStorageScope(owner)}chat:`;
  const threadAdapter = useMemo(
    () =>
      createLocalStorageAdapter({
        storage: getChatStorage(),
        prefix: storagePrefix,
        titleGenerator: {
          generateTitle: async (messages) => firstText(messages),
        },
      }),
    [owner, storagePrefix],
  );

  const attachments = useMemo(() => new SimpleImageAttachmentAdapter(), []);
  const chatModel = useMemo<ChatModelAdapter>(
    () => ({
      run: async ({ messages, abortSignal }) => {
        const result = await runRemoteAgent(
          messages,
          leads,
          owner,
          config,
          abortSignal,
          mode,
        );
        const executed = await executeAgentResult(
          result,
          leads,
          apply,
          owner,
          mode,
        );
        return {
          content: [
            ...(executed.reasoning?.trim()
              ? [
                  {
                    type: "reasoning" as const,
                    text: executed.reasoning.trim(),
                  },
                ]
              : []),
            { type: "text" as const, text: executed.text },
            ...(executed.receipt
              ? [
                  {
                    type: "data" as const,
                    name: "lead-change-receipt",
                    data: executed.receipt,
                  },
                ]
              : []),
          ],
        };
      },
    }),
    [apply, config, leads, mode, owner],
  );

  // assistant-ui 在其运行时组件中调用此回调，因此这里按 Hooks 规则使用。
  /* eslint-disable react-hooks/rules-of-hooks */
  const runtimeHook = useCallback(() => {
    const aui = useAui();
    const injectedHistory = useRuntimeAdapters()?.history;
    const history = useMemo(
      () =>
        injectedHistory
          ? withMessageDeletion(
              injectedHistory,
              getChatStorage(),
              storagePrefix,
              async () => (await aui.threadListItem.initialize()).remoteId,
            )
          : undefined,
      [aui, injectedHistory],
    );
    return useLocalRuntime(chatModel, {
      adapters: { attachments, ...(history ? { history } : {}) },
    });
  }, [attachments, chatModel, storagePrefix]);
  /* eslint-enable react-hooks/rules-of-hooks */
  const runtime = useRemoteThreadListRuntime({
    adapter: threadAdapter,
    runtimeHook,
  });

  // 记住每个用户最后打开的会话，下次进入 Agent 直接恢复
  const lastThreadKey = `${accountStorageScope(owner)}agent-last-thread`;
  useEffect(() => {
    let cancelled = false;
    void runtime.threads.getLoadThreadsPromise().then(() => {
      if (cancelled) return;
      const saved = localStorage.getItem(lastThreadKey);
      if (!saved) return;
      const { threadIds, mainThreadId } = runtime.threads.getState();
      if (threadIds.includes(saved) && saved !== mainThreadId) {
        void runtime.threads.switchToThread(saved);
      }
    });
    const persist = () => {
      const { mainThreadId, newThreadId } = runtime.threads.getState();
      if (mainThreadId && mainThreadId !== newThreadId) {
        localStorage.setItem(lastThreadKey, mainThreadId);
      }
    };
    persist();
    const unsubscribe = runtime.threads.subscribe(persist);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [runtime, lastThreadKey]);

  return (
    <ReceiptProvider
      owner={owner}
      apply={apply}
      applyContactChanges={restoreContacts}
    >
      <AssistantRuntimeProvider runtime={runtime}>
        <TooltipProvider>
          <section
            aria-label="交易 Agent"
            data-agent-mode={mode}
            className="agent-shell h-full min-h-0 overflow-hidden bg-card"
          >
            <div className="flex h-full min-h-0">
              <aside className="agent-session-sidebar hidden w-44 shrink-0 flex-col border-r bg-muted/20">
                <div className="min-h-0 flex-1 overflow-y-auto p-2 pt-3">
                  <ThreadList />
                </div>
              </aside>
              <div className="flex min-w-0 flex-1 flex-col">
                <div
                  {...dragHandleProps}
                  className="flex min-h-12 cursor-move items-center justify-between gap-2 border-b px-2 select-none"
                >
                  <div className="flex min-w-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="agent-session-trigger size-8"
                      onClick={() => setSessionsOpen(true)}
                      aria-label="打开会话列表"
                      title="会话"
                    >
                      <PanelLeft />
                    </Button>
                    <h2 className="truncate px-1 text-sm font-semibold">
                      Agent
                    </h2>
                  </div>
                  <div className="flex shrink-0 items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => setSettingsOpen(true)}
                      aria-label="设置"
                      title="连接设置"
                    >
                      <Settings2 />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={onClose}
                      aria-label="关闭 Agent"
                      title="关闭"
                    >
                      <X />
                    </Button>
                  </div>
                </div>
                <div className="agent-mode-bar flex items-center border-b px-2 py-1.5">
                  <div className="flex gap-0.5" aria-label="Agent 模式">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="对话模式"
                      title="对话模式 · 只读"
                      aria-pressed={mode === "chat"}
                      onClick={() => setMode("chat")}
                      className={`size-8 ${mode === "chat" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
                    >
                      <MessagesSquare />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="工作模式"
                      title="工作模式 · 整理入库"
                      aria-pressed={mode === "work"}
                      onClick={() => setMode("work")}
                      className={`size-8 ${mode === "work" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
                    >
                      <Wrench />
                    </Button>
                  </div>
                </div>
                <div className="min-h-0 flex-1">
                  <Thread components={threadComponents} />
                </div>
              </div>
            </div>
          </section>

          <Dialog open={sessionsOpen} onOpenChange={setSessionsOpen}>
            <DialogContent
              className="flex h-[80dvh] flex-col"
              onClick={(event) => {
                if (
                  (event.target as HTMLElement).closest(
                    "[data-slot='aui_thread-list-item-trigger'], [data-slot='aui_thread-list-new']",
                  )
                )
                  setSessionsOpen(false);
              }}
            >
              <DialogHeader>
                <DialogTitle>会话</DialogTitle>
              </DialogHeader>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <ThreadList />
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Agent 连接</DialogTitle>
                <DialogDescription>
                  由服务端统一连接，无需在浏览器填写 API Key。
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <label className="block space-y-1.5 text-sm font-medium">
                  模型
                  <Input
                    value={config.model}
                    onChange={(event) =>
                      saveConfig({ ...config, model: event.target.value })
                    }
                  />
                </label>
                <div className="flex justify-end">
                  <Button onClick={() => setSettingsOpen(false)}>完成</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TooltipProvider>
      </AssistantRuntimeProvider>
    </ReceiptProvider>
  );
}
